/**
 * Admin: Failed Verification Jobs
 *
 * Lists phoneBatchJobs across all clients in attention statuses
 * (failed / enqueue_failed by default). Lets the admin retry or cancel.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { PortalHeader } from '../Layout/PortalHeader';

interface JobSummary {
  jobId: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  status: string;
  stage?: string;
  runType: string;
  cellOnly: boolean;
  errorMessage?: string;
  estimatedCost: number;
  billedPhoneCount: number;
  totalSubmitted: number;
  createdAt: string;
  updatedAt: string;
  enqueueAttempts?: number;
  pollAttempts?: number;
  amountPaid?: number;
  source?: string;
}

const STATUS_FILTERS: { id: string; label: string; statuses: string[] }[] = [
  { id: 'attention', label: 'Needs attention', statuses: ['failed', 'enqueue_failed'] },
  { id: 'failed', label: 'Failed only', statuses: ['failed'] },
  { id: 'enqueue', label: 'Enqueue failed only', statuses: ['enqueue_failed'] },
  { id: 'awaiting', label: 'Awaiting payment', statuses: ['awaiting_payment'] },
  { id: 'in_progress', label: 'In progress', statuses: ['paid', 'uploaded_disconnect', 'uploaded_reverse'] },
  { id: 'complete', label: 'Complete', statuses: ['complete'] },
  { id: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
];

export function VerificationJobs() {
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('attention');
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [busyJobId, setBusyJobId] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filter = STATUS_FILTERS.find(f => f.id === activeFilter)!;
      const fn = httpsCallable(functions, 'listVerificationJobs');
      const result = await fn({ statusFilter: filter.statuses, limit: 200 });
      const data = result.data as { jobs: JobSummary[] };
      setJobs(data.jobs || []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleRetry = async (job: JobSummary) => {
    if (!confirm(`Retry job ${job.jobId} for ${job.clientName}?`)) return;
    setBusyJobId(job.jobId);
    setActionMessage(null);
    try {
      const fn = httpsCallable(functions, 'retryPhoneBatchJob');
      await fn({ clientId: job.clientId, jobId: job.jobId });
      setActionMessage(`Retry queued for job ${job.jobId}`);
      await loadJobs();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionMessage(`Error: ${msg}`);
    } finally {
      setBusyJobId(null);
      setTimeout(() => setActionMessage(null), 8000);
    }
  };

  const handleDownload = async (job: JobSummary) => {
    setBusyJobId(job.jobId);
    setActionMessage(null);
    try {
      const fn = httpsCallable<
        { clientId: string; jobId: string },
        { fileName: string; csvBase64: string; recordCount: number }
      >(functions, 'downloadAdminImdataResults');
      const result = await fn({ clientId: job.clientId, jobId: job.jobId });
      const { fileName, csvBase64, recordCount } = result.data;
      // base64 → Blob → object URL → click → revoke
      const bin = atob(csvBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setActionMessage(`Downloaded ${recordCount.toLocaleString()} rows`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionMessage(`Error: ${msg}`);
    } finally {
      setBusyJobId(null);
      setTimeout(() => setActionMessage(null), 8000);
    }
  };

  const handleCancel = async (job: JobSummary) => {
    const reason = prompt(`Cancel job ${job.jobId}? Optional reason:`);
    if (reason === null) return;  // user clicked Cancel on the prompt
    setBusyJobId(job.jobId);
    setActionMessage(null);
    try {
      const fn = httpsCallable(functions, 'cancelPhoneBatchJob');
      await fn({ clientId: job.clientId, jobId: job.jobId, reason });
      setActionMessage(`Job ${job.jobId} cancelled`);
      await loadJobs();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionMessage(`Error: ${msg}`);
    } finally {
      setBusyJobId(null);
      setTimeout(() => setActionMessage(null), 8000);
    }
  };

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/admin" className="back-link">Back</Link>
          <h1 style={{ margin: 0, fontSize: '1.2rem' }}>Verification Jobs</h1>
        </div>
        <button onClick={loadJobs} disabled={loading} className="btn btn-secondary">
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="container">
        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={activeFilter === f.id ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {actionMessage && (
          <div style={{
            padding: '12px 16px', marginBottom: '16px', borderRadius: '8px',
            background: actionMessage.startsWith('Error') ? 'var(--red-bg)' : 'var(--green-bg)',
            color: actionMessage.startsWith('Error') ? 'var(--red)' : 'var(--green)',
            border: `1px solid ${actionMessage.startsWith('Error') ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`
          }}>
            {actionMessage}
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px 16px', marginBottom: '16px', borderRadius: '8px',
            background: 'var(--red-bg)', color: 'var(--red)',
            border: '1px solid rgba(239,68,68,0.3)'
          }}>
            {error}
          </div>
        )}

        {loading && jobs.length === 0 && <div className="loading">Loading...</div>}

        {!loading && jobs.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray-400)' }}>
            No jobs found in this status.
          </div>
        )}

        {jobs.length > 0 && (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Client</th>
                  <th>Job ID</th>
                  <th>Run Type</th>
                  <th>Phones</th>
                  <th>Cost</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <>
                    <tr key={job.jobId}>
                      <td>
                        <span style={statusBadge(job.status)}>{job.status}</span>
                        {job.stage && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '2px' }}>stage: {job.stage}</div>}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{job.clientName}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{job.clientEmail}</div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{job.jobId.slice(0, 12)}...</td>
                      <td>{
                        job.runType === 'disconnect_plus_reverse' ? 'Disc + Reverse'
                        : job.runType === 'reverse' ? 'Reverse only'
                        : 'Disc only'
                      }{job.cellOnly && ' (cell only)'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {job.totalSubmitted.toLocaleString()}
                        {job.billedPhoneCount > 0 && job.billedPhoneCount !== job.totalSubmitted && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                            {job.billedPhoneCount.toLocaleString()} billed
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>${job.estimatedCost.toFixed(2)}</td>
                      <td style={{ fontSize: '0.78rem' }}>{job.updatedAt ? new Date(job.updatedAt).toLocaleString() : ''}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setExpandedJobId(expandedJobId === job.jobId ? null : job.jobId)}
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                          >
                            {expandedJobId === job.jobId ? 'Hide' : 'Details'}
                          </button>
                          {(job.status === 'failed' || job.status === 'enqueue_failed') && (
                            <button
                              onClick={() => handleRetry(job)}
                              disabled={busyJobId === job.jobId}
                              className="btn btn-primary"
                              style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                            >
                              Retry
                            </button>
                          )}
                          {job.status === 'complete' && (
                            <button
                              onClick={() => handleDownload(job)}
                              disabled={busyJobId === job.jobId}
                              className="btn btn-primary"
                              style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                            >
                              {busyJobId === job.jobId ? '...' : 'Download CSV'}
                            </button>
                          )}
                          {job.status !== 'cancelled' && job.status !== 'complete' && (
                            <button
                              onClick={() => handleCancel(job)}
                              disabled={busyJobId === job.jobId}
                              className="btn btn-danger"
                              style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedJobId === job.jobId && (
                      <tr key={job.jobId + '-details'}>
                        <td colSpan={8} style={{ background: 'var(--dark-2)', padding: '16px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', fontSize: '0.85rem' }}>
                            <div><strong>Job ID</strong><br/><code style={{ fontSize: '0.75rem' }}>{job.jobId}</code></div>
                            <div><strong>Client ID</strong><br/><code style={{ fontSize: '0.75rem' }}>{job.clientId}</code></div>
                            <div><strong>Source</strong><br/>{job.source || 'upload'}</div>
                            <div><strong>Created</strong><br/>{job.createdAt ? new Date(job.createdAt).toLocaleString() : '-'}</div>
                            <div><strong>Enqueue attempts</strong><br/>{job.enqueueAttempts ?? '-'}</div>
                            <div><strong>Poll attempts</strong><br/>{job.pollAttempts ?? '-'}</div>
                            <div><strong>Amount paid</strong><br/>{job.amountPaid !== undefined ? `$${job.amountPaid.toFixed(2)}` : '-'}</div>
                            <div><strong>Manage client</strong><br/><Link to={`/admin/client/${job.clientId}`} style={{ color: 'var(--gold)' }}>Open profile</Link></div>
                          </div>
                          {job.errorMessage && (
                            <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px' }}>
                              <strong style={{ color: 'var(--red)' }}>Error:</strong>
                              <pre style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', fontSize: '0.78rem', color: 'var(--gray-200)' }}>{job.errorMessage}</pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function statusBadge(status: string): React.CSSProperties {
  const colors: Record<string, { bg: string; fg: string }> = {
    failed:          { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444' },
    enqueue_failed:  { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444' },
    cancelled:       { bg: 'rgba(120,120,120,0.15)', fg: '#999' },
    complete:        { bg: 'rgba(16,185,129,0.15)', fg: '#10b981' },
    awaiting_payment:{ bg: 'rgba(200,168,78,0.15)', fg: '#c8a84e' },
    paid:            { bg: 'rgba(59,130,246,0.15)', fg: '#3b82f6' },
    uploaded_disconnect: { bg: 'rgba(59,130,246,0.15)', fg: '#3b82f6' },
    uploaded_reverse:    { bg: 'rgba(59,130,246,0.15)', fg: '#3b82f6' },
  };
  const c = colors[status] || { bg: 'rgba(120,120,120,0.15)', fg: '#aaa' };
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '999px',
    background: c.bg,
    color: c.fg,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  };
}
